import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  MUNILY_FILE_DROP_UPLOADER_DEFAULTS,
  MunilyFileDropUploaderFileIcon,
} from './file-drop-uploader.types';

interface UploadedFileVm {
  name: string;
  size: number;
  file: File;
  icon: MunilyFileDropUploaderFileIcon;
}

@Component({
  selector: 'common-file-drop-uploader',
  templateUrl: './file-drop-uploader.component.html',
  styleUrls: ['./file-drop-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDropUploaderComponent {
  @Input() label = '';
  @Input() title = '';
  @Input() hint = '';
  @Input() acceptedFormats = MUNILY_FILE_DROP_UPLOADER_DEFAULTS.acceptedFormats;
  @Input() maxSizeInBytes = MUNILY_FILE_DROP_UPLOADER_DEFAULTS.maxSizeInBytes;
  @Input() maxSizeText = MUNILY_FILE_DROP_UPLOADER_DEFAULTS.maxSizeText;
  @Input() multiple = MUNILY_FILE_DROP_UPLOADER_DEFAULTS.multiple;
  @Input() disabled = MUNILY_FILE_DROP_UPLOADER_DEFAULTS.disabled;

  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() filesRemoved = new EventEmitter<File[]>();

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  public uploadedFiles: UploadedFileVm[] = [];
  public hasError = false;
  public errorMessage = '';
  public isDragOver = false;

  constructor(
    private readonly _translate: TranslateService,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  get formatsLine(): string {
    return this._translate.instant('common-components.fileUploader.formatText', {
      acceptedFormats: this.acceptedFormats,
      maxSizeText: this.maxSizeText,
    });
  }

  get displayTitle(): string {
    if (this.title.trim().length > 0) {
      return this.title;
    }
    return this._translate.instant('common-components.fileUploader.selectFile');
  }

  get displayHint(): string {
    if (this.hint.trim().length > 0) {
      return this.hint;
    }
    return this._translate.instant('common-components.fileUploader.selectFileDescription');
  }

  get showFormatsLine(): boolean {
    return this.hint.trim().length === 0;
  }

  onZoneClick(): void {
    if (this.disabled) {
      return;
    }
    this.fileInput?.nativeElement.click();
  }

  onZoneKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onZoneClick();
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.isDragOver = true;
    this._cdr.markForCheck();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    this._cdr.markForCheck();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (this.disabled) {
      return;
    }
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this._applyFiles(files);
    }
    this._cdr.markForCheck();
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length > 0) {
      this._applyFiles(files);
    }
    input.value = '';
    this._cdr.markForCheck();
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    const remaining = this.uploadedFiles.map((item) => item.file);
    this.filesRemoved.emit(remaining);
    this.filesSelected.emit(remaining);
    this._cdr.markForCheck();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 KB';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  private _applyFiles(fileList: FileList): void {
    if (!this.multiple && fileList.length > 1) {
      this._setError('common-components.fileUploader.errors.multipleFilesNotAllowed');
      return;
    }

    const next: UploadedFileVm[] = [];
    for (let i = 0; i < fileList.length; i += 1) {
      const file = fileList[i];
      const validation = this._validateFile(file);
      if (!validation.isValid) {
        this._setError(validation.errorKey, validation.errorParams);
        this.uploadedFiles = [];
        this.filesSelected.emit([]);
        return;
      }
      next.push({
        name: file.name,
        size: file.size,
        file,
        icon: this._fileIcon(file.name),
      });
    }

    this.uploadedFiles = next;
    this.hasError = false;
    this.errorMessage = '';
    this.filesSelected.emit(next.map((item) => item.file));
  }

  private _validateFile(file: File): {
    isValid: boolean;
    errorKey: string;
    errorParams?: Record<string, string>;
  } {
    const extension = `.${(file.name.split('.').pop() ?? '').toLowerCase()}`;
    const accepted = this.acceptedFormats
      .split(',')
      .map((format) => format.trim().toLowerCase());

    if (!accepted.includes(extension)) {
      return { isValid: false, errorKey: 'common-components.fileUploader.errors.invalidFormat' };
    }

    if (file.size > this.maxSizeInBytes) {
      return {
        isValid: false,
        errorKey: 'common-components.fileUploader.errors.fileTooLarge',
        errorParams: { maxSizeText: this.maxSizeText },
      };
    }

    return { isValid: true, errorKey: '' };
  }

  private _fileIcon(fileName: string): MunilyFileDropUploaderFileIcon {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.pdf')) {
      return 'pdf';
    }
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png') || lower.endsWith('.webp')) {
      return 'image';
    }
    return 'draft';
  }

  private _setError(errorKey: string, errorParams?: Record<string, string>): void {
    this.hasError = true;
    this.errorMessage = this._translate.instant(errorKey, errorParams);
  }
}
